import axios from 'axios';

/**
 * The one axios instance the panel uses.
 *
 * ## Where the access token lives
 *
 * In a module-scoped variable, and nowhere else. Not `localStorage`, not
 * `sessionStorage`, not a cookie the page can read. Any of those survives a
 * reload, which sounds like the point until you notice what else it means: an
 * XSS anywhere in the panel can read it and walk away with an operator session.
 * Held in memory it dies with the tab, and any script that wants it has to be
 * running inside this page at the time.
 *
 * What survives a reload instead is the **refresh cookie** — httpOnly, so
 * JavaScript cannot read it at all, and path-scoped to `/api/v1/admin/auth`, so
 * it is not even sent on the two hundred list requests the panel makes. On boot
 * the app calls `/admin/auth/refresh` once and gets a fresh access token back.
 * See routes/AuthProvider.jsx.
 *
 * ## Why the refresh is single-flight
 *
 * The refresh token **rotates on every use** and the API treats a second
 * presentation of a rotated token as a compromise — it revokes the entire
 * session family and signs the operator out. So a dashboard that fires six
 * queries, all of which 401 at the same moment, must produce exactly one
 * refresh call. Six would be one success and five self-inflicted "your session
 * has been revoked" errors.
 *
 * That is what `refreshPromise` below is: the first 401 starts a refresh and
 * every subsequent one awaits the same promise, then replays with the new
 * token.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/* -------------------------------------------------------------------------- */
/* In-memory token                                                            */
/* -------------------------------------------------------------------------- */

let accessToken = null;

/** Called by the auth provider after a login or a refresh. */
export const setAccessToken = (token) => {
  accessToken = token ?? null;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
  accessToken = null;
};

/* -------------------------------------------------------------------------- */
/* Session-lost notification                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Called when a refresh fails and the session is genuinely over.
 *
 * A callback rather than a `window` event so there is exactly one subscriber
 * and it is a known one — the auth provider registers it on mount. An event
 * bus here would let any component decide what "signed out" means.
 *
 * @type {(reason: { message: string, revoked: boolean }) => void}
 */
let onSessionLost = () => {};

export const setSessionLostHandler = (handler) => {
  onSessionLost = typeof handler === 'function' ? handler : () => {};
};

/* -------------------------------------------------------------------------- */
/* The instance                                                               */
/* -------------------------------------------------------------------------- */

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },

  /**
   * Required for the refresh cookie to travel at all.
   *
   * With the Vite proxy in development the requests are same-origin and this
   * changes little; pointed at a different origin it is the difference between
   * a working session and one that silently cannot refresh. It also means the
   * API's `CORS_ORIGIN` must name this origin explicitly — a wildcard is
   * rejected when credentials are enabled.
   */
  withCredentials: true,

  // Long enough for the heavier aggregations (/admin/stats over a large
  // dataset), short enough that a hung request surfaces as an error the
  // operator can retry rather than a spinner that never resolves.
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/* -------------------------------------------------------------------------- */
/* Single-flight refresh                                                      */
/* -------------------------------------------------------------------------- */

/** The in-flight refresh, or `null`. Every concurrent 401 awaits this one. */
let refreshPromise = null;

/**
 * A bare axios call, deliberately not `api`.
 *
 * Using the instance would put this request through the response interceptor
 * below, so a failing refresh would trigger a refresh, recursively. It also
 * skips the request interceptor, which is correct: the refresh endpoint
 * authenticates by cookie and a stale bearer token on it is noise at best.
 */
const requestRefresh = () =>
  axios
    .post(
      `${BASE_URL}/admin/auth/refresh`,
      /*
       * An empty object, and it has to be — not `null` and not omitted.
       *
       * axios serialises a `null` body to the literal four bytes `null`, and
       * the API parses bodies with `express.json({ strict: true })`, which
       * accepts only an object or an array at the top level. A bare `null`
       * therefore comes back as 400 "Malformed JSON in request body" before the
       * route is ever reached — which on *this* endpoint means the session
       * cannot be restored on page load at all, and every reload looks like a
       * sign-out.
       *
       * The endpoint takes no body; `{}` is simply the smallest thing both ends
       * agree is valid JSON.
       */
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        timeout: 15_000,
      },
    )
    .then((response) => response.data?.data?.accessToken ?? null);

/**
 * Refreshes the session, collapsing concurrent callers onto one request.
 *
 * Exported because the boot sequence calls it directly: restoring a session on
 * page load is the same operation as recovering from a 401, and having two
 * implementations of it would mean two chances to get the rotation wrong.
 *
 * @returns {Promise<string|null>} the new access token, or null if the session
 *   is over
 */
export const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = requestRefresh()
      .then((token) => {
        setAccessToken(token);
        return token;
      })
      .catch((error) => {
        clearAccessToken();
        throw error;
      })
      .finally(() => {
        // Cleared in a `finally` so a failed refresh does not wedge every
        // future attempt behind a rejected promise.
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    /*
     * Refresh once, and only for a 401 on a request that has not already been
     * retried. A second 401 after a successful refresh means the token is fine
     * and the *operator* is not permitted — retrying that forever would be a
     * loop with a valid credential in it.
     */
    if (status !== 401 || !original || original._retried || original.skipAuthRefresh) {
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      const token = await refreshSession();
      if (!token) throw error;

      original.headers = { ...original.headers, Authorization: `Bearer ${token}` };
      return await api(original);
    } catch (refreshError) {
      /*
       * The session is over. Distinguish a plain expiry from a *revocation* —
       * the API says `revoked` when a rotated refresh token was replayed, which
       * means either a race the client lost or a stolen cookie. The operator is
       * told which, because "you were signed out" and "your session was revoked
       * for security reasons" call for different reactions.
       */
      const message = refreshError.response?.data?.message ?? 'Your session has expired.';
      onSessionLost({ message, revoked: /revoked/i.test(message) });

      return Promise.reject(error);
    }
  },
);

/* -------------------------------------------------------------------------- */
/* Error shaping                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Turns an axios failure into something a toast and a form can both use.
 *
 * The API's error envelope carries a `errors[]` array of `{ field, message }`,
 * and rendering it field by field is the difference between "Validation failed"
 * and "dailyROI must be a percentage between 0 and 100". Every mutation in this
 * panel surfaces both.
 *
 * @param {unknown} error
 * @returns {{ message: string, fields: Array<{ field: string, message: string }>, status: number|null }}
 */
export const toApiError = (error) => {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data;

  if (body?.message) {
    return {
      status,
      message: body.message,
      fields: Array.isArray(body.errors) ? body.errors : [],
    };
  }

  if (error?.code === 'ECONNABORTED') {
    return { status, message: 'The request timed out. Try again.', fields: [] };
  }

  if (!error?.response) {
    return {
      status,
      message: 'Could not reach the API. Check that the backend is running.',
      fields: [],
    };
  }

  return { status, message: error?.message ?? 'Something went wrong.', fields: [] };
};

/** Unwraps the API's `{ success, message, data, meta }` envelope. */
export const unwrap = (response) => ({
  ...(response.data?.data ?? {}),
  meta: response.data?.meta ?? null,
  message: response.data?.message ?? '',
});

export default api;
